import axios from "axios";
import { useQuery } from "react-query";
import { nuclearn_api_url } from "src/App";
import {
  parseSearchResults,
  searchResultHref,
  searchResultKind,
} from "src/api/search";
import type { SearchHit } from "src/api/search";
import type {
  GlobalSearchResult,
  PaginatedDatasetResponse,
} from "../types/globalSearch.types";
import {
  SEARCH_STALE_TIME,
  MAX_RESULTS,
} from "../constants/globalSearch.constants";

// ============================================
// API Functions
// ============================================

/**
 * Keyword search - searches datasets by name, description, metadata
 * Uses POST /datasets/search
 * Searches ALL folders (not just root-level) for global search
 */
export const keywordSearchDatasets = async (
  query: string,
  page: number = 1,
  pageSize: number = MAX_RESULTS,
  signal?: AbortSignal,
): Promise<PaginatedDatasetResponse> => {
  const response = await axios.post(
    `${nuclearn_api_url}/datasets/search`,
    { search_query: query, search_all_folders: true },
    {
      params: { page, page_size: pageSize, sort_by: "name", sort_desc: false },
      signal,
    },
  );
  return response.data;
};

/**
 * Hybrid search - RRF pipeline (semantic + keyword blended)
 * Uses POST /search/
 */
export const hybridSearchContent = async (
  query: string,
  nResults: number = MAX_RESULTS,
  signal?: AbortSignal,
): Promise<SearchHit[]> => {
  const response = await axios.post(
    `${nuclearn_api_url}/search/`,
    { search_query: query },
    {
      params: {
        with_sources: true,
        n_results: nResults,
      },
      signal,
    },
  );
  return parseSearchResults(response.data).hits;
};

// ============================================
// Transform Functions
// ============================================

/**
 * Transform keyword search results to unified format
 */
const transformKeywordResults = (
  data: PaginatedDatasetResponse,
): GlobalSearchResult[] => {
  return data.results.map((dataset) => ({
    id: `dataset-${dataset.id}`,
    type: "dataset" as const,
    title: dataset.name,
    description: dataset.description,
    highlight: undefined,
    metadata: {
      datasetId: dataset.id,
      datasetName: dataset.name,
    },
    navigateTo: `/datasets/${dataset.id}/viewer`,
  }));
};

/**
 * Transform hybrid search results to unified format, one per chunk; a hit
 * with no resolvable record route is dropped.
 */
export const transformHybridResults = (
  hits: SearchHit[],
  searchQuery: string,
): GlobalSearchResult[] => {
  return hits.flatMap(({ chunk, record: sourceRecord, position }, index) => {
    const navigateTo = searchResultHref(chunk, sourceRecord, searchQuery);
    if (!navigateTo) return [];
    const recordData = sourceRecord.data;

    // Get AI-extracted metadata (enriched by LLM during document processing)
    const extractedMetadata =
      (recordData?.extracted_metadata as Record<string, unknown>) || {};

    // The item renders `extractedTitle` over this, so it stays the file name.
    const fileName =
      [recordData.file_name, recordData.name].find(
        (value): value is string => typeof value === "string" && value !== "",
      ) ?? `Document ${sourceRecord.id}`;

    // Extract additional metadata - prefer AI-extracted metadata first
    const extractedTitle =
      (extractedMetadata?.title as string) ||
      (recordData?.title as string) ||
      (recordData?.document_title as string) ||
      (recordData?.extracted_title as string);

    // Authors can be a list in extracted_metadata
    const authorsArray = extractedMetadata?.authors as string[] | undefined;
    const author =
      (authorsArray?.length ? authorsArray.join(", ") : undefined) ||
      (extractedMetadata?.author as string) ||
      (recordData?.author as string) ||
      (recordData?.creator as string) ||
      (recordData?.extracted_author as string);

    const documentType =
      searchResultKind(sourceRecord) || (recordData?.mime_type as string);

    const createdDate =
      (extractedMetadata?.creation_date as string) ||
      (recordData?.created_date as string) ||
      (recordData?.creation_date as string) ||
      (recordData?.date as string);

    const pageCount = recordData?.page_count as number | undefined;

    // AI-enriched fields
    const summary = extractedMetadata?.summary as string | undefined;
    const keyTopics = extractedMetadata?.key_topics as string[] | undefined;

    // Truncate chunk data for display
    const chunkData = chunk.chunk_data ?? "";
    const chunkPreview =
      chunkData.length > 200 ? chunkData.substring(0, 200) + "..." : chunkData;

    return {
      id: `chunk-${chunk.id}-${index}`,
      type: "chunk" as const,
      title: fileName,
      description: chunkPreview,
      highlight: chunkData,
      metadata: {
        datasetId: sourceRecord.dataset_fk,
        documentId: sourceRecord.id,
        documentName: fileName,
        chunkId: chunk.id,
        // Extracted metadata
        extractedTitle,
        author,
        documentType,
        createdDate,
        pageCount,
        // AI-enriched metadata
        summary,
        keyTopics,
        // Keep raw source data for any additional fields
        sourceData: recordData,
      },
      navigateTo,
      relevanceScore: 1 - position * 0.01,
    };
  });
};

// ============================================
// Combined Search Function
// ============================================

interface SearchParams {
  query: string;
  keywordOnly: boolean;
  limit?: number;
  signal?: AbortSignal;
}

/**
 * Execute global search
 * - Default (keywordOnly=false): Hybrid RRF search + dataset metadata
 * - Keyword only: Just dataset name/metadata search
 * - Supports cancellation via AbortSignal
 */
const executeGlobalSearch = async (
  params: SearchParams,
): Promise<GlobalSearchResult[]> => {
  const { query, keywordOnly, limit = MAX_RESULTS, signal } = params;

  if (!query.trim()) {
    return [];
  }

  if (keywordOnly) {
    // Keyword only: just search dataset names/metadata
    const data = await keywordSearchDatasets(query, 1, limit, signal);
    return transformKeywordResults(data);
  }

  // Hybrid mode: RRF search + dataset metadata in parallel
  const [hybridResults, datasetResults] = await Promise.all([
    hybridSearchContent(query, limit, signal).catch((error) => {
      // Don't treat cancellation as an error
      if (axios.isCancel(error)) throw error;
      return [] as SearchHit[];
    }),
    keywordSearchDatasets(query, 1, 5, signal).catch((error) => {
      // Don't treat cancellation as an error
      if (axios.isCancel(error)) throw error;
      return {
        results: [],
        total_items: 0,
        total_pages: 0,
        current_page: 1,
        page_size: 5,
      };
    }),
  ]);

  const documents = transformHybridResults(hybridResults, query);
  const datasets = transformKeywordResults(datasetResults);

  // Only show datasets at top if query is a direct substring match in dataset name
  const queryLower = query.toLowerCase().trim();
  const directMatchDatasets = datasets.filter((d) =>
    d.title.toLowerCase().includes(queryLower),
  );

  // Dedupe: remove datasets that already appear in document results
  const documentDatasetIds = new Set(
    documents.map((d) => d.metadata.datasetId).filter(Boolean),
  );
  const uniqueDirectMatches = directMatchDatasets.filter(
    (d) => !documentDatasetIds.has(d.metadata.datasetId),
  );

  // Only promote direct name matches (max 3), then show hybrid results
  return [...uniqueDirectMatches.slice(0, 3), ...documents].slice(0, limit);
};

// ============================================
// React Query Hook
// ============================================

/**
 * Main search hook
 * TODO: Add request cancellation after upgrading to React Query v4+
 */
export const useGlobalSearch = (
  query: string,
  keywordOnly: boolean,
  enabled: boolean = true,
) => {
  return useQuery(
    ["globalSearch", query, keywordOnly],
    () => executeGlobalSearch({ query, keywordOnly }),
    {
      enabled: enabled && query.trim().length >= 2,
      staleTime: SEARCH_STALE_TIME,
      cacheTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      retry: 1,
    },
  );
};
